import { z } from "zod";

/**
 * The contract for client-readable NEXT_PUBLIC_* env vars: which keys exist and what
 * each may contain.
 *
 * Kept apart from publicEnv.ts so importing it has no side effects. scripts/guard-envs.ts
 * needs the contract to validate a pod's environment before the server starts, and must
 * not evaluate the reader while doing so.
 *
 * NOTE: NEXT_PUBLIC_BUILD_ID / NEXT_PUBLIC_BUILD_TIMESTAMP are intentionally NOT here.
 * They are build provenance (set from COMMIT_SHA at build), not per-env runtime config.
 */
const publicEnvShape = {
  // Turnkey embedded-wallet config. Optional: unset in local dev, provided per-env by infra.
  NEXT_PUBLIC_AUTH_PROXY_CONFIG_ID: z.string().optional(),
  NEXT_PUBLIC_BASE_URL: z.string().url(),
  NEXT_PUBLIC_ENVIRONMENT_TYPE: z.enum(["dev", "prod", "staging", "testing"]),
  NEXT_PUBLIC_MAINTENANCE_MODE: z.enum(["false", "true"]),
  NEXT_PUBLIC_POSTHOG_KEY: z.string().min(1),
  NEXT_PUBLIC_REOWN_PROJECT_ID: z.string().min(1),
  NEXT_PUBLIC_TURNKEY_API_BASE_URL: z.string().optional(),
  // Public half of the dashboard Auth Proxy API Key. No reader yet; staged for server-side signing.
  NEXT_PUBLIC_TURNKEY_API_PUBLIC_KEY: z.string().optional(),
  NEXT_PUBLIC_TURNKEY_ORG_ID: z.string().optional(),
  // Session profile that limits a passkey-recovery login to enrolling a new passkey. Created once per env on
  // the parent org; the recovery entry point stays hidden while it's blank.
  NEXT_PUBLIC_TURNKEY_RECOVERY_SESSION_PROFILE_ID: z.string().optional(),
} satisfies Record<`NEXT_PUBLIC_${string}`, z.ZodTypeAny>;

export const publicEnvSchema = z.object(publicEnvShape);

export type PublicEnv = z.infer<typeof publicEnvSchema>;

export const publicEnvKeys = Object.keys(publicEnvSchema.shape);
